"use client";

import { InterestGroup } from "../../../app/storyblok/[localeString]/support-hub/types/contact.types";
import { useMobileMenuDrawer } from "../../../helpers/MobileMenuDrawerContext";
// TODO: Re-enable inert state when search is active. Temporarily disabled
// import { useSearchActive } from "../../../helpers/SearchActiveContext";
import { analyticsFilter } from "../../../helpers/analytics";
import { FilterPanel } from "../FilterPanel/FilterPanel";
import InterestsGroups from "../InterestsGroups/InterestsGroups";
import type { TypesenseSearchFieldProps } from "../TypesenseSearchField/TypesenseSearchField";
import { MobileHomepageMenu } from "./MobileHomepageMenu";
import { useSideMenuLogic } from "./useSideMenuLogic";
import {
  CosmosButton,
  CosmosIconClose,
  CosmosIconFilter,
  CosmosText,
  CosmosTitle,
} from "@cosmos/web-react";
import classNames from "classnames/bind";
import {
  motion,
  useAnimate,
  useDragControls,
  useMotionValue,
  useTransform,
} from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import useMeasure from "react-use-measure";
import styles from "./MobileMenu.module.css";

const cx = classNames.bind(styles);

const CLOSED_POSITION = 76; // must match CSS variable `--crh-menu-collapsed-height`

export function MobileMenu({
  pageTitle,
  pageIntro,
  interestGroupsLabel,
  interestGroups,
  categoriesLabel,
  topicsLabel,
  filterPanelClearAllLabel,
  refineResultsLabel,
  showResultsLabel,
  filterResultsLabel,
  resultsLabel,
  typesenseSearch,
}: {
  pageTitle: string;
  pageIntro?: React.ReactNode;
  interestGroupsLabel: string;
  interestGroups: InterestGroup[];
  categoriesLabel: string;
  topicsLabel: string;
  filterPanelClearAllLabel: string;
  refineResultsLabel: string;
  showResultsLabel: string;
  filterResultsLabel: string;
  resultsLabel: string;
  typesenseSearch?: TypesenseSearchFieldProps;
}) {
  // TODO: Re-enable inert state when search is active. Temporarily disabled
  // const { isSearchActive } = useSearchActive();
  const {
    interestGroupSlug,
    selectedInterestGroup,
    availableCategories,
    selectedCategoriesIds,
    availableTopics,
    isTopicsDisabled,
    selectedTopicsIds,
    totalResultsCount,
    isHomepage,
    contactPageRouter,
    trackClick,
  } = useSideMenuLogic({
    interestGroups,
  });

  const { selectedSearchQuery } = contactPageRouter;

  const categoriesFilterPanel = (
    <FilterPanel
      key={selectedInterestGroup?.uuid || "none"}
      clearAllLabel={filterPanelClearAllLabel}
      items={availableCategories.map((category) => ({
        key: category.uuid,
        title: category.title,
        selected: selectedCategoriesIds.includes(category.uuid),
        href: contactPageRouter.toggleCategoryUrl({ uuid: category.uuid }),
        onClick: () => {
          trackClick();
          setHasInteracted(true);
          analyticsFilter({
            value: category.title,
            contentType: "filter",
          });
        },
      }))}
      label={categoriesLabel}
      deselectAllHref={contactPageRouter.deselectAllCategoriesUrl()}
      onDeselectAllClick={() => {
        trackClick();
        setHasInteracted(true);
      }}
    />
  );

  const topicsFilterPanel = (
    <FilterPanel
      buttonShape="rectangle"
      key={selectedCategoriesIds.join("-") || "none"}
      clearAllLabel={filterPanelClearAllLabel}
      disabled={isTopicsDisabled}
      selected={isTopicsDisabled ? false : undefined}
      items={availableTopics.map((topic) => {
        const selected = selectedTopicsIds.includes(topic.uuid);
        return {
          key: topic.uuid,
          title: topic.topicName,
          selected,
          href: contactPageRouter.toggleTopicUrl({ uuid: topic.uuid }),
          onClick: () => {
            trackClick();
            setHasInteracted(true);
            contactPageRouter.toggleTopicUrl({
              uuid: topic.uuid,
            });
            analyticsFilter({
              value: topic.topicName,
              contentType: "filter",
            });
          },
        };
      })}
      label={topicsLabel}
      deselectAllHref={contactPageRouter.deselectAllTopicsUrl()}
      onDeselectAllClick={() => {
        trackClick();
        setHasInteracted(true);
      }}
    />
  );

  const [open, setOpen] = useState(false);
  const [dragProgress, setDragProgress] = useState(1); // Start at 1 since drawer is closed by default
  const handleToggleRef = useRef<(() => void) | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const prevOpenRef = useRef(false);

  // Track initial interest group when menu opens (for comparison)
  const initialInterestGroupRef = useRef<string | undefined>(interestGroupSlug);

  // Reset open state when on homepage
  useEffect(() => {
    if (isHomepage) {
      setOpen(false);
    }
  }, [isHomepage]);

  // Reset interaction state when menu opens or closes
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    const justClosed = !open && prevOpenRef.current;

    if (justOpened) {
      // Menu just opened - reset interaction state and store initial interest group
      setHasInteracted(false);
      initialInterestGroupRef.current = interestGroupSlug;
    } else if (justClosed) {
      // Menu just closed - reset interaction state for next time (prevents flicker on next open)
      setHasInteracted(false);
    }

    prevOpenRef.current = open;
  }, [open, interestGroupSlug]);

  // Track interest group changes (since they're in a separate component)
  useEffect(() => {
    if (open && interestGroupSlug !== initialInterestGroupRef.current) {
      setHasInteracted(true);
    }
  }, [open, interestGroupSlug]);

  // Search results supersede the bottom-sheet entirely (homepage variant
  // included), so it is fully unmounted — offscreen and inaccessible —
  // while `?q=...` is set. Mirrors the desktop behaviour that hides the
  // filter panels in this state. Checked before `isHomepage` so the
  // homepage's `MobileHomepageMenu` is also hidden during search.
  if (selectedSearchQuery) {
    return null;
  }

  if (isHomepage) {
    return (
      <MobileHomepageMenu
        pageTitle={pageTitle}
        pageIntro={pageIntro}
        interestGroupsLabel={interestGroupsLabel}
        interestGroups={interestGroups}
        typesenseSearch={typesenseSearch}
      />
    );
  }

  return (
    <>
      <DragCloseDrawer
        open={open}
        setOpen={setOpen}
        onDragProgress={setDragProgress}
        onToggleRef={(handleToggle) => {
          handleToggleRef.current = handleToggle;
        }}
        // isSearchActive={isSearchActive}
      >
        <div className={cx("container")}>
          {/* <div className={cx("container-expander")}>
            <div className={cx("container-expander-inner")}> */}
          <div
            className={cx("header-group")}
            style={
              {
                "--header-collapsed-opacity":
                  isNaN(dragProgress) || dragProgress < 0.5 ? 0 : 1,
                "--header-expanded-opacity":
                  isNaN(dragProgress) || dragProgress >= 0.5 ? 0 : 1,
              } as React.CSSProperties
            }
          >
            <div className={cx("header-collapsed")}>
              <div className={cx("header-cosmos-wrapper")}>
                <CosmosText tag="p" size="x-small" weight="medium" clamp={1}>
                  {selectedInterestGroup?.title ?? ""}
                </CosmosText>
                <CosmosText tag="p" kind="subtle" size="xx-small" clamp={1}>
                  {totalResultsCount} {resultsLabel || "Results"}
                </CosmosText>
              </div>
              <CosmosButton
                size="small"
                className={cx("header-filter-button")}
                onClick={() => {
                  handleToggleRef.current?.();
                }}
              >
                <CosmosIconFilter slot="icon" />
                {filterResultsLabel || "Filter Results"}
              </CosmosButton>
            </div>
            <div className={cx("header-expanded")}>
              <div className={cx("header-cosmos-wrapper")}>
                <CosmosText tag="p" size="x-small" weight="medium" clamp={1}>
                  {selectedInterestGroup?.title ?? ""}
                </CosmosText>
                <CosmosText tag="p" kind="subtle" size="xx-small" clamp={1}>
                  {totalResultsCount} {resultsLabel || "Results"}
                </CosmosText>
              </div>
              <CosmosButton
                className={cx("header-filter-button")}
                onClick={() => {
                  handleToggleRef.current?.();
                }}
                size="small"
                buttonAriaLabel="Close"
                shape="circle"
                kind="secondary"
                appearance="dark"
              >
                <CosmosIconClose slot="icon" />
              </CosmosButton>
            </div>
          </div>
          <div className={cx("mobile-scroll")}>
            <div className={cx("mobile-scroll-inner")}>
              <div className={cx("introduction")}>
                <CosmosTitle
                  tag="h1"
                  size="large"
                  appearance="dark"
                  weight="bold"
                  className={cx("title")}
                >
                  {pageTitle}
                </CosmosTitle>

                {/* TODO: Prose to allow for links */}
                {pageIntro && (
                  <CosmosText
                    tag="div"
                    size="small"
                    appearance="dark"
                    kind="subtle"
                    className={cx("intro")}
                  >
                    {pageIntro}
                  </CosmosText>
                )}
              </div>
              <InterestsGroups
                interestGroupsLabel={interestGroupsLabel}
                interests={interestGroups}
                dragProgress={dragProgress}
                isCompact={true}
                hasMobileCarousel={true}
                hideTitle={true}
                appearance="dark"
              />
              <div
                className={cx("filter-panels-expander")}
                aria-hidden={!selectedInterestGroup}
              >
                <div className={cx("filter-panels-expander-inner")}>
                  <div className={cx("filter-panel-wrap")}>
                    <CosmosText
                      className={cx("filter-panel-title")}
                      tag="h2"
                      appearance="dark"
                      weight="medium"
                      size="small"
                    >
                      <CosmosIconFilter
                        className={cx("filter-panel-title-icon")}
                      />
                      {refineResultsLabel}
                    </CosmosText>
                    <div
                      className={cx("filter-panels")}
                      hidden={!selectedInterestGroup}
                    >
                      {categoriesFilterPanel}
                      {topicsFilterPanel}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={cx("mobile-menu-footer")}>
            <CosmosButton
              kind="primary"
              appearance="dark"
              disabled={!open || !hasInteracted}
              onClick={() => {
                handleToggleRef.current?.();
              }}
            >
              {showResultsLabel || "Show Results"}
            </CosmosButton>
          </div>
          {/* </div> */}
          {/* </div>  */}
        </div>
      </DragCloseDrawer>
    </>
  );
}

const DragCloseDrawer = ({
  open,
  setOpen,
  children,
  onDragProgress,
  isHomepage = false,
  onToggleRef,
  // TODO: Re-enable inert state when search is active. Temporarily
  // isSearchActive = false,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  children: React.ReactNode;
  onDragProgress?: (progress: number) => void;
  isHomepage?: boolean;
  onToggleRef?: (handleToggle: () => void) => void;
  // isSearchActive?: boolean;
}) => {
  const { registerDrawer } = useMobileMenuDrawer();
  const [scope, animate] = useAnimate();
  const [drawerRef, { height }] = useMeasure();
  const drawerRef_focus = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isInitialMount, setIsInitialMount] = useState(true);

  const y = useMotionValue(0);
  const controls = useDragControls();

  // For Draging vs Click, we will use mouse movement vs some kind of timer to detect if it's a drag or a click
  // State for handling click vs drag detection
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPointerDown, setIsPointerDown] = useState(false);

  const DRAG_THRESHOLD = 5; // pixels - threshold to determine if it's a drag vs click

  const dragProgress = useTransform(
    y,
    [0, Math.max(1, height - CLOSED_POSITION)], // Input range: [fully open, fully closed]
    [0, 1], // Output range: [0% fade, 100% fade]
  );

  // Determine if drawer is in resting closed state
  const isRestingClosed = !open && !isDragging && !isAnimating;

  // Detect when scrolled 100px down and set isInitialMount to false
  useEffect(() => {
    if (isHomepage || !isInitialMount) return;

    const handleScroll = () => {
      if (window.scrollY >= 200 && isInitialMount) {
        setIsInitialMount(false);
      }
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomepage, isInitialMount]);

  // Reset drawer position to 0 when on homepage
  // also resets the drawer from homepage -> interest group selection
  useEffect(() => {
    if (isHomepage) {
      y.set(0);
      setOpen(true); // Ensure drawer is open on homepage
    }
  }, [isHomepage, setOpen, y]);

  // TODO: HACK: there is a 1 frame flash of content as the height changes. We need a better way.
  // Recalculate closed position when height changes and drawer is closed on mobile
  useEffect(() => {
    if (!open && !isHomepage && height > 0) {
      const closedPosition = height - CLOSED_POSITION;
      y.set(closedPosition);
    }
  }, [height, open, isHomepage, y]);

  // cleanup
  useEffect(() => {
    const unsubscribe = dragProgress.on("change", (latest) => {
      // Ensure we don't pass NaN values
      const safeProgress = isNaN(latest) ? 0 : Math.max(0, Math.min(1, latest));

      // Defer the state update to avoid setState during render
      // Cannot update a component (`SideMenu`) while rendering a different component (`DragCloseDrawer`).
      requestAnimationFrame(() => {
        onDragProgress?.(safeProgress);
      });
    });

    return unsubscribe;
  }, [dragProgress, onDragProgress]);

  // Handle pointer cancellation and window blur to reset drag state
  useEffect(() => {
    const handlePointerCancel = () => {
      setIsDragging(false);
      setIsPointerDown(false);
    };

    const handleWindowBlur = () => {
      setIsDragging(false);
      setIsPointerDown(false);
    };

    const handlePointerLeave = () => {
      // Only reset if we're not in a drag operation
      if (isDragging) {
        setIsDragging(false);
        setIsPointerDown(false);
      }
    };

    // Add event listeners
    document.addEventListener("pointercancel", handlePointerCancel);
    document.addEventListener("pointerleave", handlePointerLeave);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("pointercancel", handlePointerCancel);
      document.removeEventListener("pointerleave", handlePointerLeave);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [isDragging]);

  const handleClose = useCallback(async () => {
    const yStart = typeof y.get() === "number" ? y.get() : 0;
    setIsAnimating(true);
    setIsDragging(false); // Reset dragging state when animation starts

    // Scroll content to top when closing
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });

    await animate("#drawer", {
      y: [yStart, height - CLOSED_POSITION],
    });

    setOpen(false);
    setIsAnimating(false);
  }, [animate, y, height, setOpen]);

  const handleOpen = useCallback(async () => {
    const yStart = typeof y.get() === "number" ? y.get() : 0;
    setIsAnimating(true);
    setIsDragging(false); // Reset dragging state when animation starts

    await animate("#drawer", {
      y: [yStart, 0],
    });

    setOpen(true);
    setIsAnimating(false);
  }, [animate, y, setOpen]);

  const handleToggle = useCallback(() => {
    if (open) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [open, handleClose, handleOpen]);

  // Expose handleToggle to parent component
  useEffect(() => {
    onToggleRef?.(handleToggle);
  }, [handleToggle, onToggleRef]);

  // Register drawer open function with context
  useEffect(() => {
    const openDrawer = () => {
      // Assume menu is always visible - set isInitialMount to false immediately
      if (isInitialMount) {
        setIsInitialMount(false);
      }
      // Open the drawer if it's not already open
      if (!open) {
        handleOpen();
      }
    };
    registerDrawer(openDrawer);
  }, [open, isInitialMount, handleOpen, registerDrawer]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle Enter and Space key presses for accessibility
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle();
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(false);
    setIsPointerDown(true);
    setDragStartPosition({ x: e.clientX, y: e.clientY });
    controls.start(e);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    // Only check for drag if we're in a pointer down state
    if (isPointerDown && !isDragging) {
      const deltaX = Math.abs(e.clientX - dragStartPosition.x);
      const deltaY = Math.abs(e.clientY - dragStartPosition.y);

      if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
        setIsDragging(true);
      }
    }
  };

  const handlePointerUp = () => {
    if (!isDragging) {
      // It was a click, not a drag - toggle the drawer
      handleToggle();
    }
    // Don't reset isDragging here - let the drag handlers manage it
    setIsPointerDown(false);
  };

  const handlePointerCancel = () => {
    // Don't reset isDragging here - let the drag handlers manage it
    setIsPointerDown(false);
  };

  // Focus trap functionality
  useEffect(() => {
    if (!open || isHomepage) return;

    const drawer = drawerRef_focus.current;
    if (!drawer) return;

    const focusableElements = drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      } else if (e.key === "Escape") {
        handleClose();
      }
    };

    // Focus the first element when drawer opens
    firstElement?.focus();

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, isHomepage, handleClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open && !isHomepage) {
      document.body.setAttribute("data-sidepanel-open", "true");
    } else {
      document.body.removeAttribute("data-sidepanel-open");
    }

    return () => {
      document.body.removeAttribute("data-sidepanel-open");
    };
  }, [open, isHomepage]);

  return (
    <>
      <motion.div ref={scope} className={cx("drawer-container")}>
        {/* Backdrop overlay */}
        <div
          className={cx("drawer-backdrop")}
          data-open={open && !isHomepage}
          onPointerDown={isHomepage ? undefined : handlePointerDown}
          onPointerMove={isHomepage ? undefined : handlePointerMove}
          onPointerUp={isHomepage ? undefined : handlePointerUp}
          onPointerCancel={isHomepage ? undefined : handlePointerCancel}
          style={
            {
              "--backdrop-opacity": isHomepage
                ? "0"
                : `${1 - dragProgress.get()}`,
            } as React.CSSProperties
          }
        />
        <motion.div
          data-resting-closed={isRestingClosed}
          data-offscreen={isInitialMount}
          // TODO: Re-enable inert state when search is active. Temporarily
          // disabled because the side menu can get stuck inert after closing a
          // question modal that was opened from a search result. Revisit later.
          /* inert={isSearchActive} */
          id="drawer"
          ref={(node) => {
            drawerRef(node);
            if (node) {
              drawerRef_focus.current = node;
            }
          }}
          onClick={(e) => e.stopPropagation()}
          initial={{ y: "0%" }}
          animate={false}
          transition={{
            ease: "easeInOut",
          }}
          className={cx("drawer")}
          style={{ y }}
          drag={isHomepage ? false : "y"}
          dragControls={isHomepage ? undefined : controls}
          onDragStart={() => {
            if (!isHomepage) {
              setIsDragging(true);
            }
          }}
          onDragEnd={
            isHomepage
              ? undefined
              : () => {
                  const dragDistance = y.get();
                  const closedPosition = height - CLOSED_POSITION;
                  const openThreshold = closedPosition - 50;

                  if (open && dragDistance >= 100) {
                    handleClose();
                  } else if (!open && dragDistance <= openThreshold) {
                    handleOpen();
                  } else {
                    // TODO: not sure this is the best way to do this...
                    // basically forcing it open or closed based on it's current state
                    if (open) {
                      handleOpen();
                    } else {
                      handleClose();
                    }
                  }

                  // Reset pointer state after drag ends
                  setIsPointerDown(false);
                  // Don't reset isDragging here - let the animation handlers manage it
                }
          }
          dragListener={isHomepage ? false : false}
          dragConstraints={
            isHomepage
              ? undefined
              : {
                  top: 0,
                  bottom: height,
                }
          }
          // stop dragging up when expanded
          dragElastic={
            isHomepage
              ? undefined
              : {
                  top: 0,
                  bottom: 1,
                }
          }
        >
          <div className={cx("handle-container")} hidden={isHomepage}>
            <button
              onPointerDown={
                isHomepage || isInitialMount ? undefined : handlePointerDown
              }
              onPointerMove={
                isHomepage || isInitialMount ? undefined : handlePointerMove
              }
              onPointerUp={
                isHomepage || isInitialMount ? undefined : handlePointerUp
              }
              onPointerCancel={
                isHomepage || isInitialMount ? undefined : handlePointerCancel
              }
              onKeyDown={
                isHomepage || isInitialMount ? undefined : handleKeyDown
              }
              className={cx("handle")}
              style={{ cursor: isHomepage ? "default" : undefined }}
              disabled={isHomepage || isInitialMount}
              aria-label={open ? "Close menu" : "Open menu"}
            >
              <div className={cx("handle-bar")}></div>
            </button>
          </div>
          <div
            ref={scrollContainerRef}
            className={cx("scroll-container")}
            data-scroll-disabled={!open || isAnimating}
          >
            {children}
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};
